import dayjs from 'dayjs'

import { unavailableUserIds } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleAvailability'
import type { DoctorAvailabilityGroup } from 'features/Schedule/api/schedule.types'

type GroupVisitInput = {
  availability: DoctorAvailabilityGroup[] | undefined
  // Null until the EMR providers load; the calendar checks nobody until then either.
  emrLinkedUserIds: Set<number> | null
  staffIds: number[]
  time: Date | string | undefined
  duration: number
}

// Same rule as the calendar slot click, each doctor alone: only EMR-linked doctors carry availability.
export const awayStaffIdsForVisit = ({
  availability,
  emrLinkedUserIds,
  staffIds,
  time,
  duration,
}: GroupVisitInput): number[] => {
  if (!time || !emrLinkedUserIds) return []
  const start = dayjs(time)
  if (!start.isValid()) return []
  const linkedIds = staffIds.filter((id) => emrLinkedUserIds.has(id))
  // No zone: form times are already site wall-clock digits.
  return unavailableUserIds(
    availability,
    linkedIds,
    start.toDate(),
    start.add(Math.max(duration, 5), 'minute').toDate(),
  )
}

type KeyInput = {
  staffId: number | undefined
  day: string
  time: Date | string | undefined
  duration: number
  jointStaffIds?: number[]
}

// Identifies the doctors and time a user already agreed to book while away.
export const doctorAwayKey = ({ staffId, day, time, duration, jointStaffIds }: KeyInput): string =>
  [
    staffId ?? '',
    day,
    time ? dayjs(time).format('HH:mm') : '',
    duration,
    [...(jointStaffIds ?? [])].sort((left, right) => left - right).join(','),
  ].join('|')

type FormVisit = {
  consultation_staff?: number
  date?: Date | string
  time?: Date | string
  duration?: number
}

// The doctor and time a form describes, on its own day or the dialog's.
export const awayVisitOf = (values: FormVisit, fallbackDay: string): KeyInput => ({
  staffId: values.consultation_staff,
  day: dayjs(values.date ?? fallbackDay).format('YYYY-MM-DD'),
  time: values.time,
  duration: values.duration ?? 0,
})
